// BOORO: task contracts. A task is a ticket; these types add its runs, source and result.
// They live in contracts (not @booro/tasks) because the RPC contract here cannot depend on
// a package that needs the database.
import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import type { TicketStatus } from "./board.js";
import { TICKET_COMMENT_MAX_LENGTH, TicketCommentSchema, TicketSchema } from "./board.js";
import { ArtifactSchema, ArtifactWithContentSchema } from "./domain.js";
import { ThreadMessageSchema } from "./events.js";
import { Id } from "./ids.js";

export const TASK_SOURCES = ["person", "agent", "chat", "event", "routine"] as const;
export const TaskSourceSchema = z.enum(TASK_SOURCES);
export type TaskSource = z.infer<typeof TaskSourceSchema>;

/** One short sentence, readable at a glance; details belong in a comment. */
export const TASK_RESULT_MAX_LENGTH = 120;
/** The title an agent gives a task: a few words, readable at a glance. */
export const TASK_TITLE_MAX_LENGTH = 60;
export const TASK_DONE_WHEN_MAX_LENGTH = 2_000;

/** What a person sees, derived from the ticket status and the task's latest run. */
export const TASK_DISPLAY_STATUSES = [
  "new",
  "working",
  "waiting",
  "blocked",
  "done",
  "dropped",
] as const;
export type TaskDisplayStatus = (typeof TASK_DISPLAY_STATUSES)[number];

const WAITING_RUN_STATUSES = new Set(["waiting_input", "waiting_takeover"]);
const WORKING_RUN_STATUSES = new Set(["queued", "leased", "running"]);

export function taskDisplayStatus(
  status: TicketStatus,
  latestRunStatus: string | null | undefined,
): TaskDisplayStatus {
  if (status === "done") return "done";
  if (status === "closed") return "dropped";
  if (status === "review") return "waiting";
  if (latestRunStatus && WAITING_RUN_STATUSES.has(latestRunStatus)) return "waiting";
  if (status === "blocked") return "blocked";
  if (status === "doing") return "working";
  return "new";
}

/**
 * The status of a run while the agent is really working on it. A run that
 * waits for a person is no work, so it gets no status and no animation.
 */
export function taskWorkingRunStatus(runStatus: string | null | undefined): string | null {
  if (!runStatus) return null;
  return WORKING_RUN_STATUSES.has(runStatus) ? runStatus : null;
}

export const TaskMetaSchema = z.object({
  source: TaskSourceSchema,
  sourceRef: z.string().nullable(),
  doneWhen: z.string().nullable(),
  result: z.string().nullable(),
  reopenCount: z.number().int().nonnegative(),
});
export type TaskMeta = z.infer<typeof TaskMetaSchema>;

/** The model a task points at in reports. Null when no run recorded one. */
export const TaskModelRefSchema = z.object({
  modelProvider: z.string().nullable(),
  modelId: z.string().nullable(),
});
export type TaskModelRef = z.infer<typeof TaskModelRefSchema>;

export const TaskRunSchema = z.object({
  runId: Id,
  status: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  /** The model this attempt actually used, not the one it was queued with. */
  modelProvider: z.string().nullable(),
  modelId: z.string().nullable(),
});
export type TaskRun = z.infer<typeof TaskRunSchema>;

export const TASK_OUTCOMES = ["success", "reworked", "failed", "dropped", "open"] as const;
export const TaskOutcomeSchema = z.enum(TASK_OUTCOMES);
export type TaskOutcome = z.infer<typeof TaskOutcomeSchema>;

export const TaskMetricsSchema = z.object({
  outcome: TaskOutcomeSchema,
  /** Null when no run reported a cost, for example with local models. */
  costUsd: z.number().nullable(),
  workMs: z.number().int().nonnegative(),
  /** From creation to completion; null while the task is open. */
  leadMs: z.number().int().nonnegative().nullable(),
  /** What a person would have needed for this task, in minutes; null when unknown. */
  minutesSaved: z.number().int().nonnegative().nullable(),
  /**
   * The last model that actually ran on the task. Reports attribute the task to
   * it, so a task with several attempts counts once, under the model that ended it.
   */
  modelProvider: z.string().nullable(),
  modelId: z.string().nullable(),
});
export type TaskMetrics = z.infer<typeof TaskMetricsSchema>;

export const TaskStatsRowSchema = z.object({
  success: z.number().int().nonnegative(),
  reworked: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  dropped: z.number().int().nonnegative(),
  open: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
  workMs: z.number().int().nonnegative(),
  successRate: z.number().min(0).max(1).nullable(),
});
export type TaskStatsRow = z.infer<typeof TaskStatsRowSchema>;

export const TaskModelStatsRowSchema = TaskStatsRowSchema.extend(TaskModelRefSchema.shape);
export type TaskModelStatsRow = z.infer<typeof TaskModelStatsRowSchema>;

export const TaskStatsSchema = z.object({
  total: TaskStatsRowSchema,
  byBot: z.array(TaskStatsRowSchema.extend({ botId: Id })),
  /** Same task outcomes, grouped by the model that finished each task. */
  byModel: z.array(TaskModelStatsRowSchema),
});
export type TaskStats = z.infer<typeof TaskStatsSchema>;

export const TaskStatsInput = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
  botId: Id.optional(),
});
export type TaskStatsInput = z.infer<typeof TaskStatsInput>;

export const TaskDetailSchema = z.object({
  ticket: TicketSchema,
  meta: TaskMetaSchema,
  runs: z.array(TaskRunSchema),
  displayStatus: z.enum(TASK_DISPLAY_STATUSES),
  metrics: TaskMetricsSchema,
});
export type TaskDetail = z.infer<typeof TaskDetailSchema>;

export const SetTaskMetaInput = z
  .object({
    ticketId: Id,
    doneWhen: z.string().trim().max(TASK_DONE_WHEN_MAX_LENGTH).nullable().optional(),
    result: z.string().trim().max(TASK_RESULT_MAX_LENGTH).nullable().optional(),
  })
  .refine((value) => value.doneWhen !== undefined || value.result !== undefined, {
    message: "Provide doneWhen or result",
  });
export type SetTaskMetaInput = z.infer<typeof SetTaskMetaInput>;

/** What the task lists show beside each ticket. */
export const TaskSummarySchema = z.object({
  ticketId: Id,
  result: z.string().nullable(),
  displayStatus: z.enum(TASK_DISPLAY_STATUSES),
  /** The status of the run that works on the task, so the card can show it. */
  runStatus: z.string().nullable(),
});
export type TaskSummary = z.infer<typeof TaskSummarySchema>;

/** Work an agent handed on while working on a task, shown under that task. */
export const TaskSubtaskSchema = z.object({
  ticketId: Id,
  ref: z.string(),
  title: z.string(),
  displayStatus: z.enum(TASK_DISPLAY_STATUSES),
  result: z.string().nullable(),
  assignee: z.object({ botId: Id, name: z.string(), color: z.string() }).nullable(),
});
export type TaskSubtask = z.infer<typeof TaskSubtaskSchema>;

/** A task shown as one line in its agent's conversation. */
export const TaskThreadEntrySchema = z.object({
  ticketId: Id,
  ref: z.string(),
  title: z.string(),
  displayStatus: z.enum(TASK_DISPLAY_STATUSES),
  result: z.string().nullable(),
  createdAt: z.string(),
  /** The agent working on the task, set where the list spans several agents. */
  assignee: z.object({ botId: Id, name: z.string(), color: z.string() }).nullable().optional(),
  /** The task waits for the person to approve, send back or decline it. */
  waitsForReview: z.boolean(),
  /** What the task's runs attached, so the line shows them without opening the sub-chat. */
  deliverables: z.array(ArtifactSchema),
  /** The agent's open question in this task, answerable from the line. */
  ask: ThreadMessageSchema.nullable(),
  /** The work handed on to other agents, oldest first. */
  subtasks: z.array(TaskSubtaskSchema),
});
export type TaskThreadEntry = z.infer<typeof TaskThreadEntrySchema>;

/** Everything that happened while a task ran: its run messages and its comments. */
export const TaskTimelineSchema = z.object({
  messages: z.array(ThreadMessageSchema),
  comments: z.array(TicketCommentSchema),
});
export type TaskTimeline = z.infer<typeof TaskTimelineSchema>;

export const booroTasksContract = {
  forBot: oc.input(z.object({ botId: Id })).output(z.array(TaskThreadEntrySchema)),
  timeline: oc.input(z.object({ ticketId: Id })).output(TaskTimelineSchema),
  /** What the task's runs attached: the newest version of each file, oldest file first. */
  deliverables: oc.input(z.object({ ticketId: Id })).output(z.array(ArtifactSchema)),
  /** One deliverable with its content, readable by everyone who may see the task. */
  deliverable: oc
    .input(z.object({ ticketId: Id, artifactId: Id }))
    .output(ArtifactWithContentSchema),
  get: oc.input(z.object({ ticketId: Id })).output(TaskDetailSchema),
  summaries: oc.output(z.array(TaskSummarySchema)),
  /** The tasks of every office the person may see; each keeps its office. */
  board: oc.output(
    z.object({ tasks: z.array(z.object({ ticket: TicketSchema, summary: TaskSummarySchema })) }),
  ),
  setMeta: oc.input(SetTaskMetaInput).output(TaskMetaSchema),
  /** Closes a task waiting for review and counts it as failed. */
  decline: oc.input(z.object({ ticketId: Id })).output(TicketSchema),
  stats: oc.input(TaskStatsInput).output(TaskStatsSchema),
  /** Writes to the agent from the task's sub-chat; a finished task goes back to work. */
  send: oc
    .input(
      z.object({ ticketId: Id, text: z.string().trim().min(1).max(TICKET_COMMENT_MAX_LENGTH) }),
    )
    .output(z.object({ ok: z.literal(true) })),
  /**
   * Signals each change of the task's runs, live steps included, so its sub-chat reloads.
   * Silence is punctuated with a heartbeat (seq 0).
   */
  subscribe: oc
    .input(z.object({ ticketId: Id }))
    .output(eventIterator(z.object({ seq: z.number().int() }))),
  /** Stops only the runs of this task; the task then waits for the person. */
  stop: oc.input(z.object({ ticketId: Id })).output(z.object({ ok: z.literal(true) })),
};
